import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import Summary from './Summary';
import Actions from './Actions';
import ChargebackFees from './ChargebackFees';
import TransactionDetails from './TransactionDetails';
import CustomerDetails from './CustomerDetails';
import FeeStructure from './FeeStructure';
import ActivityDetails from './ActivityDetails';

import PayoutTable from 'pages/Payouts/PayoutTable';
import RefundTable from 'pages/Refunds/RefundTable';
import BreadCrumbs from 'components/BreadCrumbs';
import Loader from 'components/Loader';
import LoaderBar from 'components/LoaderBar';
import DashboardContainer from 'components/DashboardContainer';

import { transactionDetails as TEXT } from 'locales/en/transaction';
import {
  transactionByIdSelector,
  getTransactionById,
  getTransactionRefunds,
  getTransactionPayouts,
  getTransactionActions,
} from 'redux/slices/transaction';
import { useHistory } from 'react-router-dom';

const TransactionDetail = ({ location }) => {
  const eventRef = useRef();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [activeItemId, setActiveItemId] = useState();

  const { data: transaction, isLoading } = useSelector((state) =>
    transactionByIdSelector(state, id)
  );
  const isDevOrSuperAdmin = useSelector(
    (state) => state.auth.user.isDevOrSuperAdmin
  );

  const history = useHistory();

  useEffect(() => {
    const fetchOnMount = async () => {
      if (!isLoading) {
        await dispatch(getTransactionById(id));
      }
      dispatch(getTransactionRefunds({ transaction_id: id }));
      dispatch(getTransactionPayouts({ transaction_id: id }));
      dispatch(getTransactionActions({ transaction_id: id }));
    };
    fetchOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeItemId || !transaction?.modelEvents) {
      return;
    }

    if(transaction.modelEvents.data.length) {
      setActiveItemId(transaction.modelEvents.data[0].id)
    }
  }, [activeItemId, transaction])

  const onRefundPageChange = ({ selected }) => {
    return dispatch(
      getTransactionRefunds({ transaction_id: id, page: selected + 1 })
    );
  };

  const onRefundCtaClick = useCallback((refundId) => {
    if (!transaction) return;

    const relatedEvent = transaction.modelEvents.data.find(event => event.model_id === refundId.toString());

    if (relatedEvent) {
      setActiveItemId(relatedEvent.id);

      const eventElement = document.querySelector(`div[data-testid="${relatedEvent.id}"]`);

      if (eventElement) {
        eventElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }
  }, [transaction])

  const onPayoutPageChange = ({ selected }) => {
    return dispatch(
      getTransactionPayouts({ transaction_id: id, page: selected + 1 })
    );
  };

  const onActionPageChange = ({ selected }) => {
    return dispatch(
      getTransactionActions({ transaction_id: id, page: selected + 1 })
    );
  };

  return !!transaction ? (
    <>
      <LoaderBar isVisible={isLoading} />
      <BreadCrumbs screen="transaction" />
      <DashboardContainer header={(
        <span className='flex items-center justify-between' >
          {TEXT.header1}
          <span
            className='text-xs ml-3 underline font-medium cursor-pointer'
            onClick={() => history.goBack()}
          >
            {TEXT.goBack}
          </span>
        </span>
      )}>
        <Summary transaction={transaction} />
        <TransactionDetails transaction={transaction} />
        <CustomerDetails transaction={transaction} />
        <FeeStructure transaction={transaction} />
        {transaction.modelEvents?.data?.length > 0 && (
          <ActivityDetails
            ref={eventRef}
            activeItemId={activeItemId}
            onItemClick={setActiveItemId}
            data={transaction.modelEvents.data}
            isDevOrSuperAdmin={isDevOrSuperAdmin}
          />
        )}
        {!!transaction.refunds?.data?.length > 0 && (
          <RefundTable
            isDetail
            onClickCta={onRefundCtaClick}
            data={transaction.refunds.data}
            pagination={transaction.refunds.pagination}
            onPageChange={onRefundPageChange}
          />
        )}
        {!!transaction.payouts?.data?.length > 0 && (
          <PayoutTable
            isDetail
            data={transaction.payouts.data}
            pagination={transaction.payouts.pagination}
            company={transaction.company.name}
            onPageChange={onPayoutPageChange}
          />
        )}
        {!!transaction.chargebackFees?.length > 0 && (
          <ChargebackFees data={transaction.chargebackFees} />
        )}
        {!!transaction.actions?.data?.length > 0 && (
          <Actions
            data={transaction.actions.data}
            pagination={transaction.actions.pagination}
            onPageChange={onActionPageChange}
          />
        )}
      </DashboardContainer>
    </>
  ) : (
    <Loader />
  );
};

export default TransactionDetail;
